import { useEffect, useRef, useState } from 'react';

export type FadeTransition = {
    /** Apply the class that takes the element out of `display: none`. */
    display: boolean;
    /** Apply the class that fades the element in. */
    fade: boolean;
};

/**
 * Drive a two-step fade for an element that is hidden with `display: none`.
 *
 * A `display` change cannot be transitioned, so the two are applied a frame apart: `display`
 * first, so the element is laid out at its starting opacity, then `fade` once the browser has
 * a non-hidden style to animate from. Closing runs in reverse, holding `display` until the
 * fade out has finished so the element is not cut off mid-transition.
 *
 * `durationMs` must match the CSS transition, or the element is hidden early / held too long.
 */
export function useFadeTransition(open: boolean, durationMs = 300): FadeTransition {
    // Opening on the first render skips the transition, which keeps SSR and hydration identical.
    const [display, setDisplay] = useState(open);
    const [fade, setFade] = useState(open);
    const frame = useRef(0);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        cancelAnimationFrame(frame.current);
        clearTimeout(timer.current);

        if (open) {
            setDisplay(true);
            // The first frame commits `display`, the second gives the fade something to start from.
            frame.current = requestAnimationFrame(() => {
                frame.current = requestAnimationFrame(() => setFade(true));
            });
        } else {
            setFade(false);
            timer.current = setTimeout(() => setDisplay(false), durationMs);
        }

        return () => {
            cancelAnimationFrame(frame.current);
            clearTimeout(timer.current);
        };
    }, [open, durationMs]);

    return { display, fade };
}
